import { useState, useEffect, useRef } from "react";
import {
  computeMinimalUniqueChunk,
  getVisibleRangeTextOffsetsFromSnapshot,
  getVisibleTextSnapshot,
  isWithinDecoration,
  normalizeChunkAndMatchForStorage,
} from "../utils/uniqueReferenceChunk";

/**
 * Collects every element under docRoot that has a non-empty id and intersects the range.
 * When a parent and its descendant both qualify (e.g. a section and a paragraph inside it),
 * only the descendant is kept so spanning multiple blocks yields those block ids, not a
 * wrapping parent.
 *
 * Only the range's common-ancestor subtree (plus its own ancestor chain) is
 * scanned: nothing outside it can intersect the range. Converted documents put
 * an id on every element, so the previous whole-document `[id]` scan visited
 * tens of thousands of elements per selection on manual-sized documents
 * (LAR-252); the ancestors it found were dropped as redundant anyway whenever
 * any descendant qualified.
 */
export function collectIntersectingDivElements(
  range: Range,
  docRoot: Element
): HTMLElement[] {
  const commonAncestor = range.commonAncestorContainer;
  let scope: Element =
    commonAncestor instanceof Element
      ? commonAncestor
      : commonAncestor.parentElement ?? docRoot;
  if (scope !== docRoot && !docRoot.contains(scope)) {
    scope = docRoot;
  }

  const qualifies = (el: Element): el is HTMLElement => {
    if (!(el instanceof HTMLElement) || !el.id) return false;
    // Never target deleted/decoration containers (e.g. deleted ghosts) — a
    // reference must point at the clean consolidated document.
    if (isWithinDecoration(el, docRoot)) return false;
    try {
      return range.intersectsNode(el);
    } catch {
      // intersectsNode can throw for invalid ranges in edge cases
      return false;
    }
  };

  const intersecting: HTMLElement[] = [];
  // The scope and its ancestors up to docRoot contain the whole range, so they
  // qualify whenever they carry an id — the whole-document scan collected them
  // too, and the redundancy filter below keeps only the deepest.
  for (
    let el: Element | null = scope;
    el && el !== docRoot;
    el = el.parentElement
  ) {
    if (qualifies(el)) intersecting.push(el);
  }

  const doc = docRoot.ownerDocument;
  if (doc) {
    const walker = doc.createTreeWalker(scope, NodeFilter.SHOW_ELEMENT);
    let current = walker.nextNode();
    while (current) {
      if (qualifies(current as Element)) {
        intersecting.push(current as HTMLElement);
      }
      current = walker.nextNode();
    }
  }

  const withoutRedundantAncestors = intersecting.filter((el) => {
    return !intersecting.some(
      (other) => other !== el && el.contains(other)
    );
  });
  withoutRedundantAncestors.sort((a, b) => {
    const pos = a.compareDocumentPosition(b);
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
  return withoutRedundantAncestors;
}

export function useSelection() {
  const [selectedDivIds, setSelectedDivIds] = useState<string[]>([]);
  const [destinationUniqueMatchText, setDestinationUniqueMatchText] =
    useState<string>("");
  const [destinationUniqueChunkText, setDestinationUniqueChunkText] =
    useState<string>("");
  const [selectionPosition, setSelectionPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [buttonPosition, setButtonPosition] = useState<{
    top: string;
    left: string;
  } | null>(null);

  useEffect(() => {
    let selectionTimeout: NodeJS.Timeout | null = null;

    const handleSelection = () => {
      // Clear any pending selection handling
      if (selectionTimeout) {
        clearTimeout(selectionTimeout);
      }

      // Small delay to ensure selection is complete
      selectionTimeout = setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
          setSelectedDivIds([]);
          setDestinationUniqueMatchText("");
          setDestinationUniqueChunkText("");
          setSelectionPosition(null);
          return;
        }

        const range = selection.getRangeAt(0);
        if (range.collapsed) {
          setSelectedDivIds([]);
          setDestinationUniqueMatchText("");
          setDestinationUniqueChunkText("");
          setSelectionPosition(null);
          return; // No actual selection
        }

        // Get the container element (doc-root)
        const docRoot = document.querySelector(".doc-root");
        if (!docRoot) {
          setSelectedDivIds([]);
          setDestinationUniqueMatchText("");
          setDestinationUniqueChunkText("");
          setSelectionPosition(null);
          return;
        }

        // Check if selection is within doc-root
        const startContainer = range.startContainer;
        const endContainer = range.endContainer;

        const isWithinDocRoot = (node: Node): boolean => {
          let current: Node | null = node;
          while (current && current !== docRoot) {
            current = current.parentNode;
          }
          return current === docRoot;
        };

        if (
          !isWithinDocRoot(startContainer) ||
          !isWithinDocRoot(endContainer)
        ) {
          setSelectedDivIds([]);
          setDestinationUniqueMatchText("");
          setDestinationUniqueChunkText("");
          setSelectionPosition(null);
          return; // Selection is outside doc-root
        }

        const matchTextRaw = range.toString();
        const matchTextTrimmed = matchTextRaw.trim();
        if (!matchTextTrimmed) {
          setSelectedDivIds([]);
          setDestinationUniqueMatchText("");
          setDestinationUniqueChunkText("");
          setSelectionPosition(null);
          return;
        }

        const divElements = collectIntersectingDivElements(range, docRoot);
        if (divElements.length === 0) {
          setSelectedDivIds([]);
          setDestinationUniqueMatchText("");
          setDestinationUniqueChunkText("");
          setSelectionPosition(null);
          return;
        }

        // Compute against the clean consolidated text (deletions / deleted
        // ghosts excluded) so references stay resolvable regardless of the
        // "Fremhæv ændringer" toggle. Boundaries inside deleted text are clamped.
        // One snapshot serves both the text and the offsets — the DOM walk is
        // the expensive part on manual-sized documents.
        const snapshot = getVisibleTextSnapshot(docRoot as HTMLElement);
        const fullText = snapshot.fullText;
        const offsets = getVisibleRangeTextOffsetsFromSnapshot(
          docRoot as HTMLElement,
          snapshot,
          range
        );
        if (!offsets || offsets.start === offsets.end) {
          // No offsets, or the whole selection landed inside deleted text.
          setSelectedDivIds([]);
          setDestinationUniqueMatchText("");
          setDestinationUniqueChunkText("");
          setSelectionPosition(null);
          return;
        }

        const matchFromDoc = fullText.slice(offsets.start, offsets.end);
        const chunkRaw = computeMinimalUniqueChunk(
          fullText,
          offsets.start,
          offsets.end
        );
        const { chunkText, matchText } = normalizeChunkAndMatchForStorage(
          chunkRaw,
          matchFromDoc
        );

        // Calculate selection bounding box for contextual button positioning
        const selectionRect = range.getBoundingClientRect();
        const selectionPos = {
          top: selectionRect.top,
          left: selectionRect.left,
          width: selectionRect.width,
        };
        setSelectionPosition(selectionPos);

        // Set initial button position immediately based on estimates
        // Selection UI can include legislation reference-type controls (taller than a lone button)
        const estimatedButtonHeight = 200;
        const estimatedButtonWidth = 280;
        const offset = 12;
        const padding = 16;

        const spaceAbove = selectionPos.top;
        const spaceBelow =
          window.innerHeight - selectionPos.top - estimatedButtonHeight;

        let initialTop: number;
        if (spaceAbove >= estimatedButtonHeight + offset) {
          initialTop = selectionPos.top - estimatedButtonHeight - offset;
        } else if (spaceBelow >= estimatedButtonHeight + offset) {
          initialTop = selectionPos.top + offset;
        } else {
          initialTop = Math.max(
            padding,
            Math.min(
              window.innerHeight - estimatedButtonHeight - padding,
              selectionPos.top - estimatedButtonHeight / 2
            )
          );
        }

        const centerX = selectionPos.left + selectionPos.width / 2;
        const buttonLeft = centerX - estimatedButtonWidth / 2;
        const minLeft = padding;
        const maxLeft = window.innerWidth - estimatedButtonWidth - padding;
        const initialLeft = Math.max(minLeft, Math.min(maxLeft, buttonLeft));

        setButtonPosition({
          top: `${initialTop}px`,
          left: `${initialLeft}px`,
        });

        setSelectedDivIds([
          ...new Set(divElements.map((el) => el.id)),
        ]);
        setDestinationUniqueMatchText(matchText);
        setDestinationUniqueChunkText(chunkText);
      }, 100); // Small delay to ensure selection is complete
    };

    // Listen for selection changes (more reliable than mouseup)
    document.addEventListener("selectionchange", handleSelection);

    return () => {
      document.removeEventListener("selectionchange", handleSelection);
      if (selectionTimeout) {
        clearTimeout(selectionTimeout);
      }
    };
  }, []);

  // Calculate button position based on selection and button size
  useEffect(() => {
    if (!selectionPosition) {
      setButtonPosition(null);
      return;
    }

    // First, set an initial position based on estimates so the button can render
    const estimatedButtonHeight = 200;
    const estimatedButtonWidth = 280;
    const offset = 12;
    const padding = 16;

    // Calculate initial vertical position
    const spaceAbove = selectionPosition.top;
    const spaceBelow =
      window.innerHeight - selectionPosition.top - estimatedButtonHeight;

    let initialTop: number;
    if (spaceAbove >= estimatedButtonHeight + offset) {
      initialTop = selectionPosition.top - estimatedButtonHeight - offset;
    } else if (spaceBelow >= estimatedButtonHeight + offset) {
      initialTop = selectionPosition.top + offset;
    } else {
      initialTop = Math.max(
        padding,
        Math.min(
          window.innerHeight - estimatedButtonHeight - padding,
          selectionPosition.top - estimatedButtonHeight / 2
        )
      );
    }

    // Calculate initial horizontal position (center over selection)
    const centerX = selectionPosition.left + selectionPosition.width / 2;
    const buttonLeft = centerX - estimatedButtonWidth / 2;

    // Ensure button stays within viewport bounds
    const minLeft = padding;
    const maxLeft = window.innerWidth - estimatedButtonWidth - padding;
    const initialLeft = Math.max(minLeft, Math.min(maxLeft, buttonLeft));

    // Set initial position so button can render
    setButtonPosition({
      top: `${initialTop}px`,
      left: `${initialLeft}px`,
    });

    // Then, refine position based on actual button size once it's rendered
    const refinePosition = () => {
      if (!buttonRef.current) {
        // Button not rendered yet, try again on next frame
        requestAnimationFrame(refinePosition);
        return;
      }

      const button = buttonRef.current;
      const buttonRect = button.getBoundingClientRect();
      const buttonHeight = buttonRect.height;
      const buttonWidth = buttonRect.width;

      // Recalculate with actual dimensions
      const recalculatedSpaceAbove = selectionPosition.top;
      const recalculatedSpaceBelow =
        window.innerHeight - selectionPosition.top - buttonHeight;

      let refinedTop: number;
      if (recalculatedSpaceAbove >= buttonHeight + offset) {
        refinedTop = selectionPosition.top - buttonHeight - offset;
      } else if (recalculatedSpaceBelow >= buttonHeight + offset) {
        refinedTop = selectionPosition.top + offset;
      } else {
        refinedTop = Math.max(
          padding,
          Math.min(
            window.innerHeight - buttonHeight - padding,
            selectionPosition.top - buttonHeight / 2
          )
        );
      }

      const refinedCenterX =
        selectionPosition.left + selectionPosition.width / 2;
      const refinedButtonLeft = refinedCenterX - buttonWidth / 2;
      const refinedMinLeft = padding;
      const refinedMaxLeft = window.innerWidth - buttonWidth - padding;
      const refinedLeft = Math.max(
        refinedMinLeft,
        Math.min(refinedMaxLeft, refinedButtonLeft)
      );

      // Update with refined position
      setButtonPosition({
        top: `${refinedTop}px`,
        left: `${refinedLeft}px`,
      });
    };

    // Refine position after button is rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(refinePosition);
    });
  }, [selectionPosition]);

  const clearSelection = () => {
    setSelectedDivIds([]);
    setDestinationUniqueMatchText("");
    setDestinationUniqueChunkText("");
    setSelectionPosition(null);
    window.getSelection()?.removeAllRanges();
  };

  return {
    selectedDivIds,
    destinationUniqueMatchText,
    destinationUniqueChunkText,
    selectionPosition,
    buttonPosition,
    buttonRef,
    clearSelection,
  };
}
